import React, { useEffect, useRef, useState } from "react";
import "./EventCard.css";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import logo from "../../../../assets/images/logo.png";
import { Button, IconButton, Skeleton } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  DELETE_BTN,
  BUTTON_STYLES,
  STANDARD_ICON_STYLES,
} from "../../../../utils/constants";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "../../confirmationModal/ConfirmationModal";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";

export default function EventCard({
  isLoggedIn,
  id,
  eventData,
  isLoading,
  setShowNewEventForm,
}) {
  const { t, i18n } = useTranslation();

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [canToggleDescription, setCanToggleDescription] = useState(false);
  const descriptionRef = useRef(null);
  const [
    isDeleteConfirmationWindowVisible,
    setIsDeleteConfirmationWindowVisible,
  ] = useState(false);

  function showDeleteConfirmationWindow() {
    setIsDeleteConfirmationWindowVisible(true);
  }

  useEffect(() => {
    if (isDeleteConfirmationWindowVisible) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [isDeleteConfirmationWindowVisible]);

  const currentLang = i18n.language?.startsWith("uk") ? "uk" : "en";
  const locale = currentLang === "uk" ? "uk-UA" : "en-GB";
  const eventTitle = eventData?.titles?.[currentLang] || eventData?.titles?.uk || eventData?.titles?.en || "";
  const eventDescription =
    eventData?.descriptions?.[currentLang] ||
    eventData?.descriptions?.uk ||
    eventData?.descriptions?.en ||
    "";
  const startDate = eventData?.dates?.start ? new Date(eventData.dates.start) : null;
  const endDate = eventData?.dates?.end ? new Date(eventData.dates.end) : null;
  const startDay = startDate?.toLocaleDateString(locale, { day: "2-digit" });
  const startMonth = startDate?.toLocaleDateString(locale, { month: "short" }).replace(".", "");
  const startYear = startDate?.getFullYear();
  const formattedStart = startDate?.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const formattedEnd = endDate?.toLocaleDateString(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const sameDay = startDate && endDate && startDate.toDateString() === endDate.toDateString();
  const expandLabel = currentLang === "uk" ? "Читати більше" : "Read more";
  const collapseLabel = currentLang === "uk" ? "Згорнути" : "Collapse";

  useEffect(() => {
    setIsDescriptionExpanded(false);
  }, [eventDescription, currentLang]);

  useEffect(() => {
    const checkDescriptionOverflow = () => {
      const element = descriptionRef.current;
      if (!element || !eventDescription) {
        setCanToggleDescription(false);
        return;
      }

      const styles = window.getComputedStyle(element);
      const lineHeight = parseFloat(styles.lineHeight);
      const fourLinesHeight = lineHeight * 4;
      const hasMoreThanFourLines = element.scrollHeight > fourLinesHeight + 1;

      setCanToggleDescription(hasMoreThanFourLines);
      if (!hasMoreThanFourLines) {
        setIsDescriptionExpanded(false);
      }
    };

    checkDescriptionOverflow();
    window.addEventListener("resize", checkDescriptionOverflow);
    return () => window.removeEventListener("resize", checkDescriptionOverflow);
  }, [eventDescription, currentLang]);

  function toggleDescription(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsDescriptionExpanded((value) => !value);
  }

  return (
    <>
      <article className="card event-card">
        <div className="event-card__date-sheet" aria-hidden="true">
          {isLoading ? (
            <Skeleton variant="rectangular" width="100%" height="100%" />
          ) : (
            <>
              <span className="event-card__month">{startMonth}</span>
              <strong className="event-card__day">{startDay}</strong>
              <span className="event-card__year">{startYear}</span>
            </>
          )}
        </div>

        <div className="event-card__timeline" aria-hidden="true">
          <span />
        </div>

        <div className="event-card__content">
        <div className="card-link">
          <a
            href={eventData?.link}
            className="event-card__event-link"
            target={"_blank"}
            rel="noreferrer"
            aria-label={eventTitle || t("eventsList.title")}
          />
          <div className="event-details">
            <div className="date-info">
              <CalendarTodayIcon className="calendar-icon" />
              <div className="date-info__dates">
                {isLoading ? (
                  <Skeleton
                    variant="text"
                    sx={{
                      fontSize: "var(--label-font-size)",
                      width: "50px",
                      bgcolor: "var(--white-color-1)",
                    }}
                  />
                ) : (
                  <>
                    <p>{formattedStart}</p>
                    {!sameDay && <><span aria-hidden="true">—</span><p>{formattedEnd}</p></>}
                  </>
                )}
              </div>
            </div>
          </div>
          {isLoading ? (
            <Skeleton
              variant="text"
              sx={{
                fontSize: "var(--tertiary-title-font-size)",
                bgcolor: "var(--white-color-1)",
              }}
            />
          ) : (
            <h3 className="event-title">{eventTitle}</h3>
          )}
          {isLoading ? (
            <Skeleton
              variant="text"
              sx={{
                fontSize: "15px",
                width: "70%",
                bgcolor: "var(--white-color-1)",
              }}
            />
          ) : (
            <>
              <p
                ref={descriptionRef}
                id={`event-description-${id ?? "item"}`}
                className={`event-description${eventDescription ? "" : " event-description--empty"}${
                  isDescriptionExpanded ? " event-description--expanded" : ""
                }`}
                aria-hidden={!eventDescription}
              >
                {eventDescription || "\u00A0"}
              </p>
              {eventDescription && canToggleDescription && (
                <button
                  type="button"
                  className="event-description__toggle"
                  onClick={toggleDescription}
                  aria-expanded={isDescriptionExpanded}
                  aria-controls={`event-description-${id ?? "item"}`}
                >
                  {isDescriptionExpanded ? collapseLabel : expandLabel}
                </button>
              )}
            </>
          )}
          <div className="organization">
            <div className="organization__info">
              <a
                href="/"
                className="organization__logo-link"
                aria-label="ACTIVE Generation — головна сторінка"
              >
                <img alt="ACTIVE Generation" src={logo} className="org-logo" loading="lazy" />
              </a>
              <p>ACTIVE Generation</p>
            </div>
            {!isLoggedIn && (
              <Button
                endIcon={<RateReviewRoundedIcon />}
                sx={BUTTON_STYLES}
                variant="contained"
              >
                {t("joinButton")}
              </Button>
            )}
          </div>
        </div>
        <span className="event-card__brand-shape" aria-hidden="true" />
        {isLoggedIn && (
          <div className="card__btns">
            <Button
              onClick={() => showDeleteConfirmationWindow()}
              sx={DELETE_BTN}
              endIcon={<DeleteOutlineOutlinedIcon sx={{ color: "red" }} />}
              variant="outlined"
              className="delete-card-btn"
            >
              {t("deleteBtn")}
            </Button>
            <IconButton
              className="card__edit-card-btn"
              onClick={() =>
                setShowNewEventForm({
                  isShown: true,
                  eventData: eventData,
                })
              }
            >
              <DriveFileRenameOutlineRoundedIcon sx={{ ...STANDARD_ICON_STYLES, color: "#4567E6" }} />
            </IconButton>
          </div>
        )}
        </div>
      </article>
      {isDeleteConfirmationWindowVisible && (
        <ConfirmationModal
          setIsDeleteConfirmationWindowVisible={
            setIsDeleteConfirmationWindowVisible
          }
          id={id}
        />
      )}
    </>
  );
}
